import {API_BASE} from "./config";
import {ApiError, apiFetch, apiFetchBlob} from "./client";

export interface ChatMessage {
    id: string;
    seq: number;
    order_id: string;
    remote_id: string;
    sender: "client" | "trader" | "admin";
    text: string;
    created_at: string;
    file: {name: string; mime: string; size: number} | null;
    delivery_status: "pending" | "delivered" | "failed";
    error?: string;
}

export interface ChatPage {
	seen_cursor?: number;
    messages: ChatMessage[];
    has_more: boolean;
    latest_cursor: number;
    owner_available: boolean;
}

export interface ChatSummary {
    order_id: string;
    remote_id: string;
    unread_count: number;
    last_message_at: string;
}

const orderPath = (orderID: string): string => `/order-chat/${encodeURIComponent(orderID)}`;

export function chatAccess(token: string, orderID: string): Promise<{enabled: boolean}> {
    return apiFetch(`${orderPath(orderID)}/access`, token);
}

export function chatMessages(token: string, orderID: string, before?: number): Promise<ChatPage> {
    return apiFetch(`${orderPath(orderID)}/messages${before ? `?before=${before}` : ""}`, token);
}

export function chatSummaries(token: string): Promise<{items: ChatSummary[]}> {
    return apiFetch("/order-chat/summary", token);
}

export function chatSeen(token: string, orderID: string, cursor: number): Promise<void> {
    return apiFetch(`${orderPath(orderID)}/seen`, token, {
        method: "POST", body: JSON.stringify({cursor}),
    });
}

export async function sendChatMessage(token: string, orderID: string, key: string, text: string, file: File | null): Promise<ChatMessage> {
    return sendChatMessageTo(`${orderPath(orderID)}/messages`, token, key, text, file);
}

export async function sendChatMessageTo(path: string, token: string, key: string, text: string, file: File | null): Promise<ChatMessage> {
    if (!file) return apiFetch(path, token, {
        method: "POST", body: JSON.stringify({idempotency_key: key, text}),
    });
    const body = new FormData();
    body.append("idempotency_key", key);
    body.append("text", text);
    body.append("file", file);
    const response = await fetch(`${API_BASE}${path}`, {
        method: "POST", headers: {Authorization: `Bearer ${token}`}, body,
    });
    if (!response.ok) throw new ApiError(response.status, "Не удалось отправить вложение");
    return response.json() as Promise<ChatMessage>;
}

export function retryChatMessage(token: string, orderID: string, messageID: string): Promise<void> {
    return apiFetch(`${orderPath(orderID)}/messages/${encodeURIComponent(messageID)}/retry`, token, {method: "POST"});
}

export async function downloadChatFile(token: string, orderID: string, message: ChatMessage): Promise<void> {
    const blob = await apiFetchBlob(`${orderPath(orderID)}/messages/${encodeURIComponent(message.id)}/file`, token);
    saveChatFile(blob, message.file?.name || "file");
}

export function saveChatFile(blob: Blob, name: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
